/**
* RelytArt
*
* Creative Commons Attribution-NonCommercial-NoDerivatives 4.0 International
* Copyright (c) 2022 RelytArt
*
* License: https://docs.google.com/document/d/1UD7jhCYWRXSxhx4A5vMeGVTjujwLM39BmvjwA0eFFfk/edit?usp=sharing
*
*/

// node_modules
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithRedirect,
  signOut
} from "firebase/auth";

// application
import ApplicationState from "applicationstate/ApplicationState.js";

// errors
import ERRORS from "orchestration/firebase-library/errors/Errors.js";

// firebase
import CloudFunctions from "orchestration/firebase-library/cloud-functions/FirebaseCloudFunctions.js";
import Database from "orchestration/firebase-library/database/FirebaseDatabase.js";
import Storage from "orchestration/firebase-library/storage/FirebaseStorage.js";

// utils
import LoggerUtils from "utils/logger-utils/LoggerUtils.js";

class Firebase {
  static CloudFunctions = CloudFunctions;
  static Database = Database;
  static Storage = Storage;

  static domain = null;
  static isUserPrivillegeLoading = Boolean(false);
  static user = null;

  /*====================
  getters
  ====================*/
  /**
  * @returns {Object}
  */
  static getApiPaths() {
    return {
      application: String("application"),
      collections: String("collections"),
      contact: String("contact"),
      googleMapsMarkers: String("googleMapsMarkers"),
      images: String("images"),
      ingest: String("ingest"),
      items: String("items"),
      mapMarkers: String("mapMarkers"),
      permissionGroups: String("permissionGroups"),
      qrcodes: String("qrcodes"),
      transactions: String("transactions"),
      users: String("users"),
    };
  }

  /**
  * @returns {Object}
  */
  static getConfig() {
    return {
      // this firebase apiKey is public & safe
      apiKey: String("AIzaSyDu6ShzE2owb7wxfXMfcuY7nhB1TjVbWLU"),
      appId: String("1:173917022485:web:b4624524c062b76711611c"),
      authDomain: String("relytart-66f71.firebaseapp.com"),
      databaseURL: String("https://relytart-66f71-default-rtdb.firebaseio.com"),
      messagingSenderId: String("173917022485"),
      measurementId: String("G-2LBLF0DVYP"),
      projectId: String("relytart-66f71"),
      storageBucket: String("relytart-66f71.appspot.com"),
    };
  }

  /**
  * @returns {Object}
  */
  static getContactTypes() {
    return {
      developer: String("DEVELOPERS"),
      editor: String("EDITORS"),
      general: String("GENERAL"),
      reportIssue: String("ISSUE"),
      requestAccount: String("ACCOUNT"),
    };
  }

  /**
  * @returns {String}
  */
  static getDomain() {
    const { applicationEnvironment, applicationVersion } = ApplicationState.getState();
    const applicationsEnvironments = ApplicationState.getApplicationEnvironments();
    if(applicationEnvironment === applicationsEnvironments.testing) {
      return String(`domains/tst-${applicationVersion}`);
    }
    return String(Firebase.domain);
  }

  /**
  * @async
  * @returns {String}
  * @throws {Error}
  */
  static async getGoogleMapsApiKey() {
    try {
      return await Firebase.Database.getGlobalValue("googlemaps/key");
    }
    catch(error) {
      LoggerUtils.error(error);
      throw Error(ERRORS.databaseConnectionUnavailable);
    }
  }

  /**
  * @returns {User}
  * @throws {Error}
  */
  static getUser() {
    if(!Firebase.user) {
      throw Error("User is not signed in.");
    }
    return Firebase.user;
  }

  /**
  * @returns {String}
  */
  static getUserToken() {
    return String(Firebase.getUser().accessToken);
  }

  /**
  * @returns {Object}
  */
  static getPermissionTypes() {
    return {
      read: String("read"),
      write: String("write"),
    };
  }

  /**
  * @returns {Object}
  */
  static getStorageConstants() {
    return {
      prefix: String("https://firebasestorage.googleapis.com/v0"),
      suffix: String("?alt=media&token="),
    };
  }

  /**
  * @returns {Object}
  */
  static getTransactionStatuses() {
    return {
      executing: String("EXECUTING"),
      initial: String("INITIAL"),
      fail: String("FAIL"),
      success: String("SUCCESS"),
    };
  }

  /*====================
  orchestration
  ====================*/
  /**
  * @async
  * @param {String} message
  * @param {String} type
  * @throws {Error}
  */
  static async contact(message, type) {
    try {
      await Firebase.CloudFunctions.contact(
        Firebase.getDomain(),
        message,
        type,
      );
    }
    catch(error) {
      LoggerUtils.error(error);
      throw Error(ERRORS.contactServiceUnavailable);
    }
  }

  /**
  * @async
  * @param {String} path
  * @returns {Object[<any>]}
  * @throws {Error}
  */
  static async get(path) {
    try {
      return await Firebase.Database.get(Firebase.getDomain(), path);
    }
    catch(error) {
      if(String(error).includes(ERRORS.databaseValueNotFound)) {
        throw Error(error);
      }
      else {
        throw Error(ERRORS.databaseConnectionUnavailable);
      }
    }
  }

  /**
  * @async
  * @returns {String}
  */
  static async getAppVersion() {
    return await Firebase.Database.getGlobalValue("version");
  }

  /**
  * @async
  * @param {String} domain
  * @returns {String}
  */
  static async initialize(domain) {
    const config = Firebase.getConfig();
    const app = initializeApp(config);
    Database.app = app;
    Storage.app = app;

    // set apiRoot
    const apiRoot = String(`domains/${domain}`);
    Firebase.domain = String(apiRoot);
    LoggerUtils.debug(`Firebase API root: "${apiRoot}".`);

    // listen for auth events
    const auth = getAuth();
    onAuthStateChanged(auth, (user) => Firebase.onAuthStateChange(user));

    // return app version
    return Firebase.getAppVersion();
  }

  /**
  * @async
  * @param {String} email
  * @param {String} password
  * @returns {User}
  */
  static async loginWithEmailAndPassword(email, password) {
    const auth = getAuth();
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    await Firebase.validateUserPrivilege(userCredential.user);
    Firebase.user = userCredential.user;
    return Firebase.user;
  }

  /**
  * @async
  * @returns {User}
  */
  static async loginWithGoogle() {
    const auth = getAuth();
    auth.useDeviceLanguage();
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account "});
    const userCredential = await signInWithRedirect(auth, provider);
    await Firebase.validateUserPrivilege(userCredential.user);
    Firebase.user = userCredential.user;
    return Firebase.user;
  }

  /**
  * @async
  * @returns {Promise<void>}
  */
  static async logout() {
    Firebase.user = null;
    const auth = getAuth();
    return signOut(auth);
  }

  /**
  * @async
  * @param {User} user
  */
  static async onAuthStateChange(user) {
    if(user) {
      try {
        LoggerUtils.debug("Firebase user logged in.");
        await Firebase.validateUserPrivilege(user);
        Firebase.user = user;
      }
      catch {
        LoggerUtils.debug("Firebase user rejected.");
        Firebase.logout();
      }
    }
    else {
      LoggerUtils.debug("Firebase user logged out.");
      Firebase.user = null;
    }
    ApplicationState.increment();
  }

  /**
  * @param {String} email
  * @returns {Promise<UserCredential>}
  */
  static resetPassword(email) {
    const auth = getAuth();
    return sendPasswordResetEmail(auth, email);
  }

  /**
  * @async
  * @param {User} user 
  * @returns {Boolean}
  * @throws {Error}
  */
  static async validateUserPrivilege(user) {
    Firebase.isUserPrivillegeLoading = Boolean(true);
    try {
      await Firebase.CloudFunctions.validateUserPrivilege(
        Firebase.getDomain(),
        user.accessToken,
      );
      return true;
    }
    catch(error) {
      LoggerUtils.error(error);
      throw Error(ERRORS.contactServiceUnavailable);
    }
    finally {
      Firebase.isUserPrivillegeLoading = Boolean(false);
    }
  }
}
export default Firebase;
